import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import {
    SettingType,
    CodeBlock,
    BlockType,
    ValidationMessage,
    ValidationMessageType,
    SettingState,
    SettingOption,
} from '@appblocks/codegeneration/types';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';

const settingsPerWidgetType = [
    {
        widgetType: 'line',
        dependencyArray: ['VARIABLE', 'LABEL', 'MIN', 'MAX'],
    },
    {
        widgetType: 'lcd_line',
        dependencyArray: ['NAME', 'VARIABLE', 'LABEL', 'DISPLAY_LABEL', 'FILL_COLOR', 'MIN', 'MAX'],
    },
    {
        widgetType: 'button',
        dependencyArray: ['VARIABLE', 'LABEL', 'HAS_INPUT'],
    },
    {
        widgetType: 'card',
        dependencyArray: ['VARIABLE', 'LABEL', 'DECIMAL_PLACES'],
    },
    {
        widgetType: 'lcd_card',
        dependencyArray: ['NAME', 'VARIABLE', 'LABEL', 'DISPLAY_LABEL', 'COLOR', 'BACKGROUND_COLOR'],
    },
    {
        widgetType: 'switch',
        dependencyArray: ['VARIABLE', 'LABEL', 'OFF_VALUE', 'ON_VALUE', 'OFF_LABEL', 'ON_LABEL'],
    },
    {
        widgetType: 'bar',
        dependencyArray: ['VARIABLE', 'LABEL', 'MIN', 'MAX', 'DECIMAL_PLACES', 'IS_HORIZONTAL'],
    },
    {
        widgetType: 'text',
        dependencyArray: ['VARIABLE', 'LABEL', 'MIN', 'MAX'],
    },
    {
        widgetType: 'lcd_text',
        dependencyArray: ['NAME', 'COLOR', 'BACKGROUND_COLOR', 'FONT_SIZE', 'ALIGNMENT'],
    },
    {
        widgetType: 'slider',
        dependencyArray: ['VARIABLE', 'LABEL', 'MIN', 'MAX', 'IS_HORIZONTAL'],
    },
    {
        widgetType: 'gauge',
        dependencyArray: ['VARIABLE', 'LABEL', 'MIN', 'MAX', 'DECIMAL_PLACES', 'IS_HORIZONTAL'],
    },
    {
        widgetType: 'lcd_image',
        dependencyArray: ['NAME', 'IMG', 'IMG_WIDTH', 'IMG_HEIGHT'],
    },
];


export default class Widget extends BaseFeature {
    constructor() {
        super();
        this.name = 'Widget';
        this.id = 'widget';
        this.singleInstance = false;
        this.settings = this.settings.concat([
            {
                id: 'TYPE',
                name: 'Type',
                settingType: SettingType.OPTION,
                required: true,
                default: 'card',
            },
            {
                id: 'VARIABLE',
                name: 'Variable',
                settingType: SettingType.OPTION,
                required: true,
                default: '',
            },
            {
                id: 'X',
                name: 'X',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '0',
            },
            {
                id: 'Y',
                name: 'Y',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '0',
            },
            {
                id: 'W',
                name: 'W',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '4',
            },
            {
                id: 'H',
                name: 'H',
                settingType: SettingType.HIDDEN,
                required: true,
                default: '4',
            },
            {
                id: 'NAME',
                name: 'Name',
                settingType: SettingType.STRING,
                required: true,
                default: 'widget',
            },
            {
                id: 'LABEL',
                name: 'Label',
                settingType: SettingType.STRING,
                required: true,
                default: 'widget_label',
            },
            {
                id: 'MIN',
                name: 'Min',
                settingType: SettingType.NUMBER,
                required: true,
                default: '0',
            },
            {
                id: 'MAX',
                name: 'Max',
                settingType: SettingType.NUMBER,
                required: true,
                default: '100',
            },
            {
                id: 'DECIMAL_PLACES',
                name: 'Decimal Places',
                settingType: SettingType.NUMBER,
                required: true,
                default: '0',
            },
            {
                id: 'VALUES',
                name: 'Values',
                settingType: SettingType.NUMBER,
                required: true,
                default: '50',
            },
            {
                id: 'CUSTOM_FN',
                name: 'Custom Function',
                settingType: SettingType.STRING,
                required: true,
                default: '',
            },
            {
                id: 'BOUNDS',
                name: 'Bounds',
                settingType: SettingType.BOOLEAN,
                required: true,
                default: '1',
            },
            {
                id: 'HAS_INPUT',
                name: 'Has Input',
                settingType: SettingType.BOOLEAN,
                required: true,
                default: '0',
            },
            {
                id: 'ON_VALUE',
                name: 'On Value',
                settingType: SettingType.NUMBER,
                required: true,
                default: '1',
            },
            {
                id: 'OFF_VALUE',
                name: 'Off Value',
                settingType: SettingType.NUMBER,
                required: true,
                default: '0',
            },
            {
                id: 'ON_LABEL',
                name: 'On Label',
                settingType: SettingType.STRING,
                required: true,
                default: 'On',
            },
            {
                id: 'OFF_LABEL',
                name: 'Off Label',
                settingType: SettingType.STRING,
                required: true,
                default: 'Off',
            },
            {
                id: 'IS_ON',
                name: 'Is On',
                settingType: SettingType.BOOLEAN,
                required: true,
                default: '1',
            },
            {
                id: 'IS_HORIZONTAL',
                name: 'Horizontal',
                settingType: SettingType.BOOLEAN,
                required: true,
                default: '1',
            },
            {
                id: 'FILL_COLOR',
                name: 'Fill Color',
                settingType: SettingType.COLOR,
                required: true,
                default: '#00FF00',
            },
            {
                id: 'COLOR',
                name: 'Color',
                settingType: SettingType.COLOR,
                required: true,
                default: '#ffffff',
            },
            {
                id: 'BACKGROUND_COLOR',
                name: 'Background Color',
                settingType: SettingType.COLOR,
                required: true,
                default: '#000000',
            },
            {
                id: 'DISPLAY_LABEL',
                name: 'Display Label',
                settingType: SettingType.BOOLEAN,
                required: true,
                default: '1',
            },
            {
                id: 'ALIGNMENT',
                name: 'Text Alignment',
                settingType: SettingType.OPTION,
                default: 'PL_LCD_TEXT_ALIGNMENT_TOP_LEFT',
                options: [
                    {
                        name: 'Top Left',
                        value: 'PL_LCD_TEXT_ALIGNMENT_TOP_LEFT',
                    },
                    {
                        name: 'Top Center',
                        value: 'PL_LCD_TEXT_ALIGNMENT_TOP_CENTER',
                    },
                    {
                        name: 'Top Right',
                        value: 'PL_LCD_TEXT_ALIGNMENT_TOP_RIGHT',
                    },
                    {
                        name: 'Middle Left',
                        value: 'PL_LCD_TEXT_ALIGNMENT_MIDDLE_LEFT',
                    },
                    {
                        name: 'Middle Center',
                        value: 'PL_LCD_TEXT_ALIGNMENT_MIDDLE_CENTER',
                    },
                    {
                        name: 'Middle Right',
                        value: 'PL_LCD_TEXT_ALIGNMENT_MIDDLE_RIGHT',
                    },
                    {
                        name: 'Bottom Left',
                        value: 'PL_LCD_TEXT_ALIGNMENT_BOTTOM_LEFT',
                    },
                    {
                        name: 'Bottom Center',
                        value: 'PL_LCD_TEXT_ALIGNMENT_BOTTOM_CENTER',
                    },
                    {
                        name: 'Bottom Right',
                        value: 'PL_LCD_TEXT_ALIGNMENT_BOTTOM_RIGHT',
                    },
                ],
            },
            {
                id: 'ORIENTATION',
                name: 'Text Orientation',
                settingType: SettingType.OPTION,
                default: 'PL_LCD_TEXT_ORIENTATION_0',
                options: [
                    {
                        name: 'At 0 Degrees',
                        value: 'PL_LCD_TEXT_ORIENTATION_0',
                    },
                    {
                        name: 'At 90 degrees',
                        value: 'PL_LCD_TEXT_ORIENTATION_90',
                    },
                    {
                        name: 'At 180 degrees',
                        value: 'PL_LCD_TEXT_ORIENTATION_180',
                    },
                    {
                        name: 'At 270 degrees',
                        value: 'PL_LCD_TEXT_ORIENTATION_270',
                    },
                ],
            },
            {
                id: 'FONT',
                name: 'Font Size',
                settingType: SettingType.OPTION,
                required: true,
                default: 'LCD_STD_FONT',
                options: [
                    {
                        name: 'Standard Font',
                        value: 'LCD_STD_FONT',
                    },
                    {
                        name: 'Small Font',
                        value: 'LCD_SMALL_FONT',
                    },
                ],
            },
            {
                id: 'IMG',
                name: 'Image',
                settingType: SettingType.FILE,
                required: true,
                default: '',
            },
            {
                id: 'IMG_WIDTH',
                name: 'Image Width',
                settingType: SettingType.NUMBER,
                required: true,
                default: '0',
            },
            {
                id: 'IMG_HEIGHT',
                name: 'Image Height',
                settingType: SettingType.NUMBER,
                required: true,
                default: '0',
            },
        ]);
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const project: ProjectConfiguration = this.project;
        const parent = project.getParent(this);
        if (!parent) {
            return super.getSettingOptions(settingId);
        }
        let options: SettingOption[] | undefined = [];
        let projectVariables: Array<BaseFeature> = [];
        let commands = [];
        const widgetType = this.getSettingValue('TYPE');
        switch (settingId) {
            case 'TYPE':
                if (parent.id === 'lcd') {
                    options = options.concat([
                        {
                            name: 'Line',
                            value: 'lcd_line',
                        },
                        {
                            name: 'Card',
                            value: 'lcd_card',
                        },
                        {
                            name: 'Text',
                            value: 'lcd_text',
                        },
                        {
                            name: 'Image',
                            value: 'lcd_image',
                        },
                    ]);
                } else {
                    options = options.concat([
                        {
                            name: 'Line',
                            value: 'line',
                        },
                        {
                            name: 'Bar',
                            value: 'bar',
                        },
                        {
                            name: 'Gauge',
                            value: 'gauge',
                        },
                        {
                            name: 'Card',
                            value: 'card',
                        },
                        {
                            name: 'Text Input',
                            value: 'text',
                        },
                        {
                            name: 'Slider',
                            value: 'slider',
                        },
                        {
                            name: 'Switch',
                            value: 'switch',
                        },
                        {
                            name: 'Button',
                            value: 'button',
                        },
                    ]);
                }
                break;
            case 'VARIABLE':
                if (widgetType === 'button') {
                    if (project.getFeature('commands')) {
                        commands = project.getFeature('commands')?.getSettingValue('COMMANDS');
                    }
                    for (let i = 0; i < commands.length; i++) {
                        options.push({
                            value: commands[i].getID(),
                            name: commands[i].getSettingValue('NAME'),
                        });
                    }
                } else {
                    if (project.getFeature('global_vars')) {
                        projectVariables = project.getVariables();
                    }
                    for (let i = 0; i < projectVariables.length; i++) {
                        options.push({
                            value: projectVariables[i].getID(),
                            name: projectVariables[i].getSettingValue('VARNAME'),
                        });
                    }
                }
                break;
            default:
                return super.getSettingOptions(settingId);
        }
        return options;
    }

    getFiles() {
        const IMG = this.getSettingValue('IMG');
        const fileArray = [];
        if (IMG !== '') {
            fileArray.push(IMG.split('\n')[0]);
        }
        return fileArray;
    }

    getState(settingId: string): SettingState {
        if (settingId === 'TYPE'
        || settingId === 'X'
        || settingId === 'Y'
        || settingId === 'W'
        || settingId === 'H'
        ) {
            return super.getState(settingId);
        }
        const widgetType = this.getSettingValue('TYPE');
        const dependencyArray = settingsPerWidgetType
            .find(widget => widget.widgetType === widgetType)?.dependencyArray;
        if (!dependencyArray) {
            return super.getState(settingId);
        }
        for (let i = 0; i < dependencyArray.length; i++) {
            if (settingId === dependencyArray[i]) {
                return super.getState(settingId);
            }
        }
        return SettingState.HIDDEN;
    }

    getSettingValue(settingId: string, getStoredValue?: boolean) {
        if (settingId === 'FILL_COLOR') {
            for (let i = 0; i < this.settings.length; i++) {
                if (this.settings[i].id === 'FILL_COLOR') {
                    return this.settings[i].value;
                }
            }
        }
        return super.getSettingValue(settingId, getStoredValue);
    }


    validate(): Array<ValidationMessage> {
        const errors: Array<ValidationMessage> = [];


        return errors;
    }

    getCodeBlocks() {
        const blocks: Array<CodeBlock> = [];


        return blocks;
    }
}
